import * as React from 'react'
import styled from 'styled-components'
import Button from '../../../../common/src/components/Button'
import { InlineErrorBoundary } from '../../../../common/src/components/InlineErrorBoundary'
import Modal from '../../../../common/src/components/Modal'
import { Spacer, H3, IntroText, TextTag, AlignRight } from '../../../../common/src/components/core'
import { addFeedback, FeedbackType } from '../../../../common/src/feedback'
import keyboard from '../../../../common/src/keyboard'
import { FetchUserContextQuery, UserType } from '../../../../common/src/types.gen'

interface AnalyticsProps {
  /**
   * Signed-in User.
   */
  user: FetchUserContextQuery['self']
  /**
   * True if the page is live on the web. False if it's running in a local dev environment.
   */
  isProduction: boolean
}

/**
 * Track user activities. Logs all DOM changes to a remote service.
 *
 * Currently events are not being sent anymore. We could send the events to [Pendo](https://www.pendo.io/) for analytics.
 */
export default class Analytics extends React.PureComponent<
  AnalyticsProps,
  {
    /**
     * True if the current User is opted-in to Fullstory tracking
     */
    enabled: boolean
    /**
     * True if we should cover the page in a warning about copy/paste permissions
     */
    showPermissionsModal: boolean
  }
> {
  constructor(props: AnalyticsProps) {
    super(props)
    this.state = {
      enabled: this.shouldEnableForUser(this.props.user),
      showPermissionsModal: false,
    }
  }

  /**
   * When page loads, identify user to Fullstory and start tracking copy/paste + "find in page" actions.
   */
  componentDidMount() {
    if (this.shouldInit()) {
      keyboard.addMetaListener(this.onCopyCutOrPaste)
      keyboard.addMetaListener(this.onFindInPage)
    }
  }

  componentWillUnmount() {
    keyboard.removeMetaListener(this.onCopyCutOrPaste)
    keyboard.removeMetaListener(this.onFindInPage)
  }

  /**
   * Skip loading if we're in development (testing)
   */
  shouldInit = () => this.state.enabled && this.props.isProduction

  /**
   * Track just Ops staff in Fullstory. Tracking other UserTypes (ENGINEER, ANALYST, SALES) isn't useful.
   */
  shouldEnableForUser = (user: FetchUserContextQuery['self']) =>
    [UserType.OPERATOR, UserType.SENIOR_OPERATOR, UserType.TEAM_LEAD, UserType.OPS_MANAGER].includes(user.userType)

  isCopy = (e: KeyboardEvent) => (e.ctrlKey || e.metaKey) && e.code === 'KeyC' // Ctrl+C or Cmd+C pressed?

  isCut = (e: KeyboardEvent) => (e.ctrlKey || e.metaKey) && e.code === 'KeyX' // Ctrl+X or Cmd+X pressed?

  isPaste = (e: KeyboardEvent) => (e.ctrlKey || e.metaKey) && e.code === 'KeyV' // Ctrl+V or Cmd+V pressed?

  /**
   * Warn the user, then get clipboard permissions, then track copy/paste in Fullstory
   */
  private onCopyCutOrPaste = (e: KeyboardEvent) => {
    if (!this.isCopy(e) && !this.isCut(e) && !this.isPaste(e)) {
      return // short circuit if the user didn't cut/copy/paste
    }
    const name = 'clipboard-read' as PermissionName
    navigator.permissions
      .query({ name })
      .then(result => {
        switch (result.state) {
          case 'prompt':
            return this.showModal()
          case 'granted':
            return this.onCopyCutOrPasteWithPermissions(e)
          default:
            addFeedback('Clipboard permission denied', FeedbackType.ERROR)
            console.error(
              result,
              'Clipboard permission denied.\n\n' +
                'Fix by enabling "clipboard" permissions for "dynasty.com": https://tinyurl.com/enableClipboardInChrome'
            )
        }
      })
      .catch(err => {
        console.log("Couldn't detect browser clipboard permissions: ", err)
      })
  }

  /**
   * Try to grab (a) what they're copy/pasting and (b) current clipboard contents
   */
  private onCopyCutOrPasteWithPermissions = (e: KeyboardEvent) => {
    navigator.clipboard
      .readText()
      .then(text => {
        // `text` contains the text read from the clipboard
        const target = e && e.target
        const d: any = target &&
          target instanceof Element && {
            element: target.nodeName,
            className: target.className,
            id: target.id,
            text,
          }
        if (this.isCopy(e)) {
          console.log('Copy', d)
        }
        if (this.isPaste(e)) {
          console.log('Paste', d)
        }
        if (this.isCut(e)) {
          console.log('Cut', d)
        }
      })
      .catch(err => {
        // maybe user didn't grant access to read from clipboard
        addFeedback('Clipboard permission denied', FeedbackType.ERROR)
        console.log('FullStory copy/paste failed: ', err)
      })
  }

  /**
   * Show modal warning user to accept clipboard permissions
   */
  showModal = () => this.setState({ showPermissionsModal: true })

  /**
   * Hide modal warning, then fire actual permission / browser dialog
   */
  hideModal = () => {
    this.setState({ showPermissionsModal: false })
    navigator.clipboard
      .readText()
      .then(t => console.log(t))
      .catch(e => console.log(e))
  }

  /**
   * A `<Modal>` element with title + screenshot of how to accept permissions
   */
  permissionModal() {
    return (
      this.state.showPermissionsModal && (
        <Modal title={'CC wants to read the clipboard'} width={540} onClose={this.hideModal}>
          <Spacer height={24} />
          <H3>
            Please click
            <Spacer width={16} />
            <TextTag color="sky" title="the 'Allow' button will show after you [X] this prompt">
              Allow
            </TextTag>
          </H3>
          <Spacer height={24} />
          <IntroText>Your browser will ask you after you close this prompt.</IntroText>
          <Spacer height={32} />
          <Img src="/ui/cc-clipboard-permissions.png" />
          <Spacer height={64} />
          <AlignRight>
            <Button onClick={this.hideModal}>Next</Button>
          </AlignRight>
        </Modal>
      )
    )
  }

  /**
   * When user searches on the page using `ctrl + F` or `cmd + F`, emit event to Fullstory
   */
  private onFindInPage = (e: KeyboardEvent) => {
    // Ctrl+F or Cmd+F pressed?
    if ((e.ctrlKey || e.metaKey) && e.code === 'KeyF') {
      console.log('Find')
    }
  }

  render() {
    return (
      this.shouldInit() && <InlineErrorBoundary componentName="Analytics">{this.permissionModal()}</InlineErrorBoundary>
    )
  }
}

const Img = styled.img({
  maxWidth: '100%',
  borderRadius: '6px',
})
